import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import type { IdentityDetails } from 'types/publishing';
import { ipiCheck, writerName } from '../derive-ui';
import { ANOTHER_SOCIETY, SOCIETIES } from '../societies';
import { ui } from '../ui';

/** The ASCAP writer the pipeline matched for the artist's legal name. */
export interface IdentityCandidate {
  name: string;
  pro: string | null;
  ipi: string | null;
}

interface Props {
  open: boolean;
  /** With a candidate the artist judges the match first; without one the form opens directly. */
  candidate: IdentityCandidate | null;
  /** From the header "That's not me" link: skip the confirm stage (FR-02b). */
  rejectOnly: boolean;
  /** Admins see the same stages with the sending actions disabled. */
  readOnly: boolean;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onWrong: (details: IdentityDetails) => void;
}

const label = `mb-1 block ${ui.subhead}`;
const hint = 'm-0 mt-1 text-sm';
const field = `box-border h-10 ${ui.body}`;

export function IdentityDialog({
  open,
  candidate,
  rejectOnly,
  readOnly,
  busy,
  onClose,
  onConfirm,
  onWrong,
}: Props) {
  const { t } = useTranslation('publishing');
  const canConfirm = !!candidate && !rejectOnly;
  const [stage, setStage] = useState<'confirm' | 'form'>('form');
  const [society, setSociety] = useState('');
  const [otherSociety, setOtherSociety] = useState('');
  const [name, setName] = useState('');
  const [ipi, setIpi] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStage(canConfirm ? 'confirm' : 'form');
    setSociety('');
    setOtherSociety('');
    setName('');
    setIpi('');
    setTouched(false);
  }, [open, canConfirm]);

  const check = ipiCheck(ipi);
  const societyValue = society === ANOTHER_SOCIETY ? otherSociety.trim() : society;
  const canSend = !!societyValue && name.trim().length > 1 && check.valid;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-[480px] gap-0 overflow-y-auto p-6">
        <DialogTitle className={`mb-1 ${ui.subhead}`}>{t('identity_title')}</DialogTitle>
        <DialogDescription className={`mb-4 ${ui.muted}`}>
          {stage === 'confirm'
            ? t('identity_candidate_description')
            : t('identity_form_description')}
        </DialogDescription>

        {stage === 'confirm' && candidate ? (
          <>
            <div className="mb-4 box-border rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <div className={ui.subhead}>
                {t('identity_registered_as', { name: writerName(candidate.name) })}
              </div>
              <div className={`mt-1 tabular-nums ${ui.muted}`}>
                {[
                  candidate.pro,
                  candidate.ipi && t('identity_songwriter_id', { ipi: candidate.ipi }),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Button disabled={busy || readOnly} onClick={onConfirm} className={ui.btnPrimary}>
                {t('identity_thats_me')}
              </Button>
              <Button className={ui.btnPrimary} onClick={() => setStage('form')}>
                {t('identity_not_me')}
              </Button>
              <Button variant="outline" className={ui.btnSecondary} onClick={onClose}>
                {t('detail_close')}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="mb-4 flex flex-col gap-4">
              <div>
                <span className={label}>{t('identity_society_label')}</span>
                <Select value={society || undefined} onValueChange={setSociety}>
                  <SelectTrigger
                    aria-label={t('identity_society_label')}
                    className={`w-full ${field}`}
                  >
                    <SelectValue placeholder={t('identity_society_placeholder')} />
                  </SelectTrigger>
                  <SelectContent position="popper" className="max-h-[300px]">
                    {SOCIETIES.map((s) => (
                      <SelectItem key={s} value={s} className={ui.body}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {society === ANOTHER_SOCIETY && (
                <div>
                  <label htmlFor="pub-society-other" className={label}>
                    {t('identity_society_other_label')}
                  </label>
                  <Input
                    id="pub-society-other"
                    value={otherSociety}
                    maxLength={64}
                    placeholder={t('identity_society_other_placeholder')}
                    className={field}
                    onChange={(e) => setOtherSociety(e.target.value)}
                  />
                </div>
              )}

              <div>
                <label htmlFor="pub-registered-name" className={label}>
                  {t('identity_name_label')}
                </label>
                <Input
                  id="pub-registered-name"
                  value={name}
                  maxLength={200}
                  placeholder={t('identity_name_placeholder')}
                  className={field}
                  onChange={(e) => setName(e.target.value)}
                />
                <p className={`${hint} text-[#8C8C8C]`}>{t('identity_name_hint')}</p>
              </div>

              <div>
                <label htmlFor="pub-ipi" className={label}>
                  {t('identity_ipi_label')}{' '}
                  <span className="text-sm font-normal text-[#8C8C8C]">
                    {t('identity_optional')}
                  </span>
                </label>
                <Input
                  id="pub-ipi"
                  value={ipi}
                  inputMode="numeric"
                  placeholder={t('identity_ipi_placeholder')}
                  className={`${field} tabular-nums`}
                  onChange={(e) => setIpi(e.target.value)}
                  onBlur={() => setTouched(true)}
                />
                <p className={`${hint} text-[#8C8C8C]`}>{t('identity_ipi_hint')}</p>
                {touched && !check.valid && (
                  <p className={`${hint} text-red-600`}>{t('identity_ipi_error')}</p>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Button
                disabled={busy || readOnly || !canSend}
                className={ui.btnPrimary}
                onClick={() =>
                  onWrong({
                    society: societyValue,
                    registeredName: name.trim(),
                    ipi: check.digits || undefined,
                  })
                }
              >
                {t('identity_send')}
              </Button>
              {canConfirm && (
                <Button
                  variant="outline"
                  className={ui.btnSecondary}
                  onClick={() => setStage('confirm')}
                >
                  {t('identity_back')}
                </Button>
              )}
              <Button variant="outline" className={ui.btnSecondary} onClick={onClose}>
                {t('detail_close')}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
